import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen,
  ChevronDown,
  Cloud,
  FileText,
  FolderOpen,
  Languages,
  LoaderCircle,
  LogOut,
  Menu,
  Sparkles,
  X,
} from "lucide-react";
import { AuthDialog } from "./components/AuthDialog";
import { AiContextPanel } from "./components/AiContextPanel";
import { DictionaryPanel } from "./components/DictionaryPanel";
import { Dropzone } from "./components/Dropzone";
import { InsightPanel } from "./components/InsightPanel";
import { LibraryPanel } from "./components/LibraryPanel";
import { PdfReader } from "./components/PdfReader";
import { LANGUAGES } from "./constants";
import { useAuth } from "./hooks/useAuth";
import { useDictionary } from "./hooks/useDictionary";
import { usePdfLibrary } from "./hooks/usePdfLibrary";
import {
  explainWordInContext,
  lookupContextImage,
  lookupDictionaryWord,
} from "./lib/api";
import {
  cleanupAiExplanationCache,
  readCachedAiExplanation,
  writeCachedAiExplanation,
} from "./lib/aiExplanationCache";
import { parseBook, validateBook } from "./lib/book";
import {
  getDictionaryErrorCode,
  getDictionaryErrorMessage,
} from "./lib/dictionaryErrors";
import {
  cleanupExpiredInsights,
  readCachedInsight,
  writeCachedInsight,
} from "./lib/insightCache";
import { speakText } from "./lib/speech";
import { formatBytes, normalizeWordId, tokenizePage } from "./lib/text";
import type {
  AiContextExplanation,
  AiContextImage,
  DictionaryWord,
  ParsedPdf,
  StoredPdf,
  WordInsight,
} from "./types";

type View = "reader" | "dictionary" | "library";

type Toast = {
  message: string;
  tone: "success" | "error" | "info";
};

export default function App() {
  const auth = useAuth();
  const dictionary = useDictionary(auth.user);
  const library = usePdfLibrary();
  const [view, setView] = useState<View>("reader");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [targetLanguageCode, setTargetLanguageCode] = useState("tr-TR");
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [parsedPdf, setParsedPdf] = useState<ParsedPdf | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [parseProgress, setParseProgress] = useState<{ current: number; total: number } | null>(
    null,
  );
  const [selectedWord, setSelectedWord] = useState("");
  const [selectedContext, setSelectedContext] = useState("");
  const [insight, setInsight] = useState<WordInsight | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [aiExplanation, setAiExplanation] = useState<AiContextExplanation | null>(null);
  const [aiExplanationLoading, setAiExplanationLoading] = useState(false);
  const [aiExplanationError, setAiExplanationError] = useState("");
  const [aiImage, setAiImage] = useState<AiContextImage | null>(null);
  const [aiImageLoading, setAiImageLoading] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [pendingCloudFile, setPendingCloudFile] = useState<File | null>(null);
  const [activeBookId, setActiveBookId] = useState<string | null>(null);
  const analysisController = useRef<AbortController | null>(null);
  const aiController = useRef<AbortController | null>(null);
  const uploadedFiles = useRef(new Set<string>());

  const targetLanguage = useMemo(
    () =>
      LANGUAGES.find((language) => language.code === targetLanguageCode) ??
      LANGUAGES[0],
    [targetLanguageCode],
  );

  const insightId = insight
    ? normalizeWordId(insight.word, targetLanguage.nativeLabel)
    : "";
  const isSaved = dictionary.words.some((word) => word.id === insightId);

  useEffect(() => {
    void cleanupExpiredInsights();
    cleanupAiExplanationCache();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!activeBookId || !parsedPdf) return;
    const timer = window.setTimeout(() => {
      void library.updateProgress(activeBookId, pageNumber);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [activeBookId, library.updateProgress, pageNumber, parsedPdf]);

  useEffect(() => {
    if (!pendingCloudFile) return;
    const key = `${pendingCloudFile.name}-${pendingCloudFile.size}-${pendingCloudFile.lastModified}`;
    if (uploadedFiles.current.has(key)) return;
    uploadedFiles.current.add(key);

    void library
      .upload(pendingCloudFile)
      .then((id) => {
        setActiveBookId(id);
        setToast({ message: "Kitap cihazındaki arşive kaydedildi.", tone: "success" });
        setPendingCloudFile(null);
      })
      .catch((reason) => {
        uploadedFiles.current.delete(key);
        setPendingCloudFile(null);
        setToast({
          message:
            reason instanceof Error
              ? `Kitap açıldı; yerel arşive kaydedilemedi: ${reason.message}`
              : "Kitap yerel arşive kaydedilemedi.",
          tone: "error",
        });
      });
  }, [library.upload, pendingCloudFile]);

  const runAiExplanation = useCallback(
    async (word: string, context: string) => {
      aiController.current?.abort();
      const controller = new AbortController();
      aiController.current = controller;
      setAiExplanation(null);
      setAiExplanationLoading(false);
      setAiExplanationError("");
      setAiImage(null);
      setAiImageLoading(false);

      if (!auth.user) {
        setAiExplanationLoading(false);
        return;
      }

      let result: AiContextExplanation;
      const cached = readCachedAiExplanation(word, context);
      if (cached) {
        result = cached;
        setAiExplanation(cached);
      } else {
        setAiExplanationLoading(true);
        try {
          const idToken = await auth.user.getIdToken();
          result = await explainWordInContext({ word, context }, idToken, controller.signal);
          if (controller.signal.aborted) return;
          setAiExplanation(result);
          writeCachedAiExplanation(word, context, result);
        } catch (reason) {
          if (reason instanceof DOMException && reason.name === "AbortError") return;
          setAiExplanationError(
            reason instanceof Error ? reason.message : "AI açıklaması hazırlanamadı.",
          );
          return;
        } finally {
          if (aiController.current === controller) setAiExplanationLoading(false);
        }
      }

      if (!result.imageQuery || controller.signal.aborted) return;
      setAiImageLoading(true);
      try {
        const image = await lookupContextImage(result.imageQuery, controller.signal);
        if (!controller.signal.aborted) setAiImage(image);
      } catch (reason) {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          console.error("Context image lookup failed", reason);
        }
      } finally {
        if (aiController.current === controller) setAiImageLoading(false);
      }
    },
    [auth.user],
  );

  const runAnalysis = useCallback(
    async (word: string, context: string) => {
      setSelectedWord(word);
      setSelectedContext(context);
      setInsight(null);
      setAnalysisError("");
      analysisController.current?.abort();
      void runAiExplanation(word, context);

      const controller = new AbortController();
      analysisController.current = controller;
      const cacheInput = {
        word,
        context,
        targetLanguageCode: targetLanguage.code,
      };

      try {
        const cached = await readCachedInsight(cacheInput);
        if (controller.signal.aborted) return;
        if (cached) {
          setInsight(cached);
          setAnalysisLoading(false);
          return;
        }

        setAnalysisLoading(true);
        const result = await lookupDictionaryWord(
          {
            word,
            context,
            targetLanguage: targetLanguage.nativeLabel,
            targetLanguageCode: targetLanguage.code,
          },
          controller.signal,
        );
        await writeCachedInsight(cacheInput, result);
        setInsight(result);
      } catch (reason) {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setAnalysisError(
          reason instanceof Error ? reason.message : "Kelime analizi tamamlanamadı.",
        );
      } finally {
        if (analysisController.current === controller) setAnalysisLoading(false);
      }
    },
    [runAiExplanation, targetLanguage],
  );

  async function handleFile(file: File, saveToArchive = true, initialPage = 1) {
    try {
      validateBook(file);
      setView("reader");
      setCurrentFile(file);
      setParsedPdf(null);
      setSelectedWord("");
      setSelectedContext("");
      setInsight(null);
      setAnalysisError("");
      aiController.current?.abort();
      setAiExplanation(null);
      setAiExplanationError("");
      setAiExplanationLoading(false);
      setAiImage(null);
      setAiImageLoading(false);
      setPageNumber(1);
      if (saveToArchive) setActiveBookId(null);
      setParseProgress({ current: 0, total: 1 });

      const parsed = await parseBook(file, (current, total) =>
        setParseProgress({ current, total }),
      );
      setParsedPdf(parsed);
      setPageNumber(Math.min(Math.max(1, initialPage), parsed.pageCount));
      setToast({
        message: `${parsed.pageCount} ${parsed.format === "epub" ? "bölüm" : "sayfa"} hazır. Şimdi bir kelime seçebilirsin.`,
        tone: "success",
      });

      if (saveToArchive) {
        setPendingCloudFile(file);
      }
    } catch (reason) {
      setCurrentFile(null);
      setParsedPdf(null);
      setToast({
        message: reason instanceof Error ? reason.message : "Kitap açılamadı.",
        tone: "error",
      });
    } finally {
      setParseProgress(null);
    }
  }

  async function openStoredPdf(document: StoredPdf) {
    try {
      setToast({ message: "Kitap yerel arşivden açılıyor…", tone: "info" });
      const file = await library.openDocument(document);
      setActiveBookId(document.id);
      await handleFile(file, false, document.lastPage ?? 1);
    } catch (reason) {
      setToast({
        message: reason instanceof Error ? reason.message : "Kitap açılamadı.",
        tone: "error",
      });
    }
  }

  async function removeStoredPdf(document: StoredPdf) {
    if (!window.confirm(`“${document.name}” belgesini hesabından silmek istiyor musun?`)) {
      return;
    }
    try {
      await library.removeDocument(document);
      setToast({ message: "Kitap arşivden silindi.", tone: "success" });
    } catch (reason) {
      setToast({
        message: reason instanceof Error ? reason.message : "Kitap silinemedi.",
        tone: "error",
      });
    }
  }

  async function saveInsight() {
    if (!insight) return;
    try {
      await dictionary.addWord(
        insight,
        selectedContext,
        targetLanguage.nativeLabel,
        insight.detectedLanguage,
      );
      setToast({ message: "Kelime sözlüğüne eklendi.", tone: "success" });
    } catch (reason) {
      console.error("Dictionary save failed", {
        code: getDictionaryErrorCode(reason),
        reason,
      });
      setToast({
        message: getDictionaryErrorMessage(reason, "save"),
        tone: "error",
      });
    }
  }

  async function removeDictionaryWord(id: string) {
    try {
      await dictionary.removeWord(id);
      setToast({ message: "Kelime sözlükten silindi.", tone: "success" });
    } catch (reason) {
      console.error("Dictionary delete failed", {
        code: getDictionaryErrorCode(reason),
        reason,
      });
      setToast({
        message: getDictionaryErrorMessage(reason, "delete"),
        tone: "error",
      });
    }
  }

  function selectDictionaryWord(word: DictionaryWord) {
    setSelectedWord(word.word);
    setSelectedContext(word.context);
    const hasDualLanguageContent = Boolean(
      word.sourceDefinition && word.translatedDefinition && word.contextTranslation,
    );
    setInsight(hasDualLanguageContent ? word : null);
    setAnalysisError("");
    void runAiExplanation(word.word, word.context);
    const matchingLanguage = LANGUAGES.find(
      (language) => language.nativeLabel === word.targetLanguage,
    );
    if (matchingLanguage) setTargetLanguageCode(matchingLanguage.code);
    setView("reader");
  }

  function navTo(nextView: View) {
    setView(nextView);
    setMobileMenuOpen(false);
  }

  function languageCodeFor(name: string | undefined, fallback = "en-US") {
    if (!name) return fallback;
    const normalized = name.toLocaleLowerCase();
    return (
      LANGUAGES.find(
        (language) =>
          language.label.toLocaleLowerCase() === normalized ||
          language.nativeLabel.toLocaleLowerCase() === normalized ||
          language.code.toLocaleLowerCase() === normalized,
      )?.code ?? fallback
    );
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" onClick={() => navTo("reader")}>
          <span className="brand-mark">V</span>
          <span>
            <strong>Vibe</strong>Translations
          </span>
        </button>

        <nav className={mobileMenuOpen ? "main-nav is-open" : "main-nav"}>
          <button
            className={view === "reader" ? "is-active" : ""}
            onClick={() => navTo("reader")}
          >
            <FileText size={18} /> Çalışma alanı
          </button>
          <button
            className={view === "dictionary" ? "is-active" : ""}
            onClick={() => navTo("dictionary")}
          >
            <BookOpen size={18} /> Sözlüğüm
            {dictionary.words.length > 0 && <b>{dictionary.words.length}</b>}
          </button>
          <button
            className={view === "library" ? "is-active" : ""}
            onClick={() => navTo("library")}
          >
            <FolderOpen size={18} /> Kitap arşivim
          </button>
        </nav>

        <div className="account-area">
          {auth.loading ? (
            <LoaderCircle className="spin" size={20} />
          ) : auth.user ? (
            <div className="profile-chip">
              {auth.user.photoURL ? (
                <img src={auth.user.photoURL} alt="" referrerPolicy="no-referrer" />
              ) : (
                <span>{(auth.user.email?.[0] ?? "M").toLocaleUpperCase()}</span>
              )}
              <div>
                <strong>{auth.user.displayName ?? "Hesabım"}</strong>
                <small>{auth.user.email}</small>
              </div>
              <button
                className="icon-button"
                onClick={() => void auth.signOutUser()}
                aria-label="Çıkış yap"
              >
                <LogOut size={17} />
              </button>
            </div>
          ) : (
            <button className="primary-button small" onClick={() => setAuthOpen(true)}>
              Giriş yap
            </button>
          )}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen((value) => !value)}
            aria-label="Menüyü aç"
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      <main>
        {view === "reader" && (
          <>
            <section className="workspace-header">
              <div>
                <p className="eyebrow"><Sparkles size={14} /> AKILLI KİTAP OKUYUCU</p>
                <h1>
                  Oku. Dokun. <em>Gerçekten öğren.</em>
                </h1>
                <p>
                  PDF veya EPUB’da bilmediğin kelimeyi seç; bağlama uygun çeviri, okunuş ve
                  örnekler anında yanında.
                </p>
              </div>
              <label className="language-select">
                <span>Çeviri dili</span>
                <div>
                  <Languages size={18} />
                  <select
                    value={targetLanguageCode}
                    onChange={(event) => {
                      setTargetLanguageCode(event.target.value);
                      if (selectedWord && auth.user) {
                        setInsight(null);
                        setAnalysisError("");
                      }
                    }}
                  >
                    {LANGUAGES.map((language) => (
                      <option key={language.code} value={language.code}>
                        {language.nativeLabel}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} />
                </div>
              </label>
            </section>

            {!currentFile && !parseProgress ? (
              <div className="starter-layout">
                <div>
                  <Dropzone onFile={(file) => void handleFile(file)} />
                  <div className="privacy-strip">
                    <Cloud size={17} />
                    <span>
                      Kitabın yalnızca cihazında işlenir ve yerel arşivinde tutulur.{" "}
                      Analiz için tüm belge değil, sadece seçtiğin kelime ve cümle gönderilir.
                    </span>
                  </div>
                </div>
                <InsightPanel
                  insight={null}
                  selectedWord=""
                  context=""
                  loading={false}
                  error=""
                  saved={false}
                  onSpeakWord={() => undefined}
                  onSpeakTranslation={() => undefined}
                  onSave={() => undefined}
                  targetLanguage={targetLanguage.nativeLabel}
                />
              </div>
            ) : parseProgress ? (
              <div className="parsing-card">
                <LoaderCircle className="spin" size={32} />
                <h2>Kitap okunuyor</h2>
                <p>
                  {parseProgress.total > 1
                    ? `${parseProgress.current} / ${parseProgress.total} sayfa`
                    : "Belge hazırlanıyor…"}
                </p>
                <div className="parse-bar">
                  <span
                    style={{
                      width: `${Math.max(
                        8,
                        (parseProgress.current / parseProgress.total) * 100,
                      )}%`,
                    }}
                  />
                </div>
              </div>
            ) : (
              currentFile &&
              parsedPdf && (
                <>
                  <div className="document-subbar">
                    <div>
                      <FileText size={18} />
                      <span>{currentFile.name}</span>
                      <small>{formatBytes(currentFile.size)}</small>
                    </div>
                    {library.uploadProgress !== null && (
                      <span className="cloud-saving">
                        <LoaderCircle className="spin" size={15} />
                        Cihaza kaydediliyor %{library.uploadProgress}
                      </span>
                    )}
                    <Dropzone onFile={(file) => void handleFile(file)} compact />
                  </div>
                  <div className="active-workspace">
                    <PdfReader
                      file={currentFile}
                      parsed={parsedPdf}
                      pageNumber={pageNumber}
                      selectedWord={selectedWord}
                      onPageChange={setPageNumber}
                      onWordSelect={(word, context) => void runAnalysis(word, context)}
                    />
                    <div className="insight-stack">
                      <InsightPanel
                        insight={insight}
                        selectedWord={selectedWord}
                        context={selectedContext}
                        loading={analysisLoading}
                        error={analysisError}
                        saved={isSaved}
                        onSpeakWord={() => {
                          try {
                            speakText(selectedWord, languageCodeFor(insight?.detectedLanguage));
                          } catch (reason) {
                            setToast({
                              message:
                                reason instanceof Error ? reason.message : "Sesli okuma açılamadı.",
                              tone: "error",
                            });
                          }
                        }}
                        onSpeakTranslation={() => {
                          try {
                            speakText(insight?.translation ?? selectedWord, targetLanguage.code);
                          } catch (reason) {
                            setToast({
                              message:
                                reason instanceof Error ? reason.message : "Sesli okuma açılamadı.",
                              tone: "error",
                            });
                          }
                        }}
                        onSave={() => void saveInsight()}
                        targetLanguage={targetLanguage.nativeLabel}
                      />
                      <AiContextPanel
                        selectedWord={selectedWord}
                        explanation={aiExplanation}
                        loading={aiExplanationLoading}
                        error={aiExplanationError}
                        image={aiImage}
                        imageLoading={aiImageLoading}
                        signedIn={Boolean(auth.user)}
                        onLogin={() => setAuthOpen(true)}
                        onRetry={() => void runAiExplanation(selectedWord, selectedContext)}
                      />
                    </div>
                  </div>
                </>
              )
            )}
          </>
        )}

        {view === "dictionary" && (
          <DictionaryPanel
            words={dictionary.words}
            loading={dictionary.loading}
            onRemove={(id) => void removeDictionaryWord(id)}
            onSpeak={(word) => speakText(word.word, languageCodeFor(word.sourceLanguage))}
            onSelect={selectDictionaryWord}
          />
        )}

        {view === "library" && (
          <LibraryPanel
            documents={library.documents}
            loading={library.loading}
            error={library.error}
            uploadProgress={library.uploadProgress}
            onFile={(file) => void handleFile(file)}
            onOpen={(document) => void openStoredPdf(document)}
            onRemove={(document) => void removeStoredPdf(document)}
          />
        )}
      </main>

      <AuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onEmailSignIn={auth.signInWithEmail}
        onCreateAccount={auth.createAccount}
      />

      {toast && (
        <div className={`toast toast-${toast.tone}`} role="status">
          {toast.message}
          <button onClick={() => setToast(null)} aria-label="Bildirimi kapat">
            <X size={17} />
          </button>
        </div>
      )}
    </div>
  );
}
